import React from 'react';
import { Grid, Typography } from '@mui/material';
import { BhiDateTimePicker } from 'src/components/Inputs/BhiDateTimePicker';
import { SectionProps } from '../../../registry/sectionRegistry';
import { EnrollmentTemplateSettings } from '../../../types';
import {
  normalizeEnrollmentTemplateSettings,
  validateEnrollmentTemplateSettings,
  REQUIRED_ENROLLMENT_TEMPLATE_FIELDS,
} from '../../../helpers';
import { SettingsControl } from '../SettingsControl';
import { toDayjs, toIso } from '../dateValue';
import { noTopGutterSx, Panel } from 'src/UIKit';

/**
 * Join Window for enrollment campaigns: the start/end during which contacts
 * may enroll. Both fields are backend-mandated, so their Visible/Required
 * toggles are locked.
 */
export const EnrollmentWindowSection: React.FC<SectionProps> = ({ config, onConfigUpdate }) => {
  const settings = normalizeEnrollmentTemplateSettings(config.enrollmentSettings);
  const errors = validateEnrollmentTemplateSettings(settings);

  const updateControl = <K extends keyof EnrollmentTemplateSettings>(
    key: K,
    next: EnrollmentTemplateSettings[K],
  ) => {
    onConfigUpdate({ enrollmentSettings: { ...settings, [key]: next } });
  };

  const isLocked = (key: keyof EnrollmentTemplateSettings) =>
    REQUIRED_ENROLLMENT_TEMPLATE_FIELDS.includes(key);

  return (
    <Grid item xs={12} sx={noTopGutterSx}>
      <Panel>
        <Typography variant="subtitle1" mb={2} fontWeight="fontWeightBold">
          Join Window
        </Typography>

        <SettingsControl
          label="Join Window Start"
          control={settings.joinStartAt}
          locked={isLocked('joinStartAt')}
          onChange={next => updateControl('joinStartAt', next)}
        >
          {(value, setValue) => (
            <BhiDateTimePicker allowEmpty value={toDayjs(value)} onChange={next => setValue(toIso(next))} />
          )}
        </SettingsControl>

        <SettingsControl
          label="Join Window End"
          control={settings.joinEndAt}
          locked={isLocked('joinEndAt')}
          onChange={next => updateControl('joinEndAt', next)}
          error={errors['enrollmentSettings.joinEndAt']}
        >
          {(value, setValue) => (
            <BhiDateTimePicker allowEmpty value={toDayjs(value)} onChange={next => setValue(toIso(next))} />
          )}
        </SettingsControl>
      </Panel>
    </Grid>
  );
};
